import { createAsyncThunk, createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit"
import { LatLngBounds } from "leaflet"
import localForage from "localforage"

import { Client, DataStatus, RootState, Team } from "@/types"
import { fetchTeams } from "@/utils"

import { clearDataOnLogout } from "./user"

const teamsAdapter = createEntityAdapter<Team>()

type TeamsState = EntityState<Team> & {
  current: Team | null
  currentMulti: Team[]
  status: DataStatus | "updating" | null
  cityBounds: LatLngBounds | null
}

const initialState: TeamsState = teamsAdapter.getInitialState({
  current: null,
  currentMulti: [],
  status: "no_team",
  cityBounds: null,
})

export const getTeams = createAsyncThunk("teams/getTeams", async () => {
  const { response, json } = await fetchTeams()

  return {
    ok: response.ok,
    status: response.status,
    data: json.data,
  }
})

const teamsSlice = createSlice({
  name: "teams",
  initialState,
  reducers: {
    setCurrentTeam(state, action: PayloadAction<Team>) {
      const teamToSet = action.payload
      if (state.ids.some((teamId) => teamId === teamToSet.id)) {
        state.current = action.payload
        state.cityBounds = null
      }
    },
    setCurrentMultiTeams(state, action: PayloadAction<Team[]>) {
      localForage.setItem("multi_teams", action.payload)
      state.currentMulti = action.payload
    },
    setCityBounds(state, action: PayloadAction<TeamsState["cityBounds"]>) {
      state.cityBounds = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTeams.pending, (state) => {
      if (state.ids.length) {
        state.status = "updating"
      } else {
        state.status = "loading"
      }
    })

    builder.addCase(getTeams.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        if (payload.data) teamsAdapter.setAll(state, payload.data)
        state.status = "fetched"
      } else {
        state.status = "error"
      }
    })

    builder.addCase(clearDataOnLogout, () => initialState)
  },
})

const { selectById, selectIds, selectAll } = teamsAdapter.getSelectors((state: RootState) => state.teams)

export const selectTeamsIds = selectIds

export const selectTeams = selectAll

export const selectTeamById = (id: Client["id"]) => (state: RootState) => selectById(state, id)

export const selectCurrentTeam = (state: RootState) => state.teams.current

export const selectCurrentTeamId = (state: RootState) => state.teams.current?.id

export const selectCurrentTeamKey =
  <T extends keyof Team>(key: T) =>
  (state: RootState) =>
    state.teams.current?.[key]

export const selectCurrentMultiTeams = (state: RootState) => state.teams.currentMulti

export const selectTeamsStatus = (state: RootState) => state.teams.status

export const selectHasCurrentTeamDeliveryZone = (state: RootState) =>
  Boolean(state.teams.current?.settings?.use_geofences && state.teams.current?.settings?.geofences_pos_id)

export const { setCurrentTeam, setCurrentMultiTeams, setCityBounds } = teamsSlice.actions
export const teamsReducer = teamsSlice.reducer
